import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment,
  Link
} from '@mui/material';
import { Visibility, VisibilityOff, Email } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import authService from '../services/authService';
import { buildInfo } from '../config/buildInfo';
import { getSiteName } from '../config/appConfig';

const steps = ['Solicitar Token', 'Restablecer Contraseña'];

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleRequestToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!username.trim()) {
      setError('El nombre de usuario es requerido');
      return;
    }

    setLoading(true);

    try {
      await authService.forgotPassword(username);
      // Mensaje genérico - siempre el mismo por seguridad
      const genericMessage = 'Si el usuario existe y tiene un email registrado, recibirás un correo con las instrucciones para restablecer tu contraseña. Por favor, revisa tu bandeja de entrada y la carpeta de spam.';
      setSuccessMessage(genericMessage);
      enqueueSnackbar('Instrucciones enviadas', { variant: 'success' });
      
      // Redirigir al paso 2 después de 3 segundos
      setTimeout(() => {
        setActiveStep(1);
      }, 3000);
    } catch (err: any) {
      let errorMessage = 'Error al procesar la solicitud';
      
      if (err.response) {
        if (err.response.status === 500) {
          errorMessage = err.response.data?.message || 'Error al procesar la solicitud. Contacta al administrador.';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.message || 'Error de validación';
        } else {
          // Por seguridad, siempre mostrar mensaje genérico
          const genericMessage = 'Si el usuario existe y tiene un email registrado, recibirás un correo con las instrucciones para restablecer tu contraseña. Por favor, revisa tu bandeja de entrada y la carpeta de spam.';
          setSuccessMessage(genericMessage);
          enqueueSnackbar('Instrucciones enviadas', { variant: 'success' });
          setTimeout(() => {
            setActiveStep(1);
          }, 3000);
          setLoading(false);
          return;
        }
      } else if (err.request) {
        errorMessage = 'Error de conexión. Por favor, intenta nuevamente';
      }

      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token.trim()) {
      setError('El token es requerido');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError('Todos los campos son requeridos');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, newPassword);
      enqueueSnackbar('Contraseña restablecida exitosamente', { variant: 'success' });
      navigate('/login', { state: { message: 'Contraseña restablecida exitosamente. Por favor, inicia sesión.' } });
    } catch (err: any) {
      let errorMessage = 'Error al restablecer la contraseña';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = err.response.data || 'Token inválido, expirado o ya utilizado';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.message || 'Error de validación';
        } else if (err.response.status === 404) {
          errorMessage = err.response.data || 'Usuario no encontrado';
        } else {
          errorMessage = err.response.data?.message || err.response.data || errorMessage;
        }
      } else if (err.request) {
        errorMessage = 'Error de conexión. Por favor, intenta nuevamente';
      }

      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      setToken('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccessMessage('');
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            component="img"
            src="/assets/eone-logo.png"
            alt="eOne"
            sx={{ height: 80, width: 'auto', mb: 2 }}
          />
          <Typography
            component="h1"
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.02em',
              fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
              ].join(','),
            }}
          >
            {getSiteName()}
          </Typography>
          <Typography component="h2" variant="h6" sx={{ mb: 3, color: 'text.secondary', fontWeight: 400 }}>
            Recuperar Contraseña
          </Typography>

          <Stepper activeStep={activeStep} sx={{ width: '100%', mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {successMessage && activeStep === 0 && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box component="form" onSubmit={handleRequestToken} sx={{ mt: 1, width: '100%' }}>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Ingresa tu nombre de usuario para recibir las instrucciones de restablecimiento por email.
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Usuario"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading || !!successMessage}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !!successMessage}
                startIcon={loading ? <CircularProgress size={20} /> : <Email />}
              >
                {loading ? 'Enviando...' : successMessage ? 'Instrucciones Enviadas' : 'Enviar Instrucciones por Email'}
              </Button>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  Volver al inicio de sesión
                </Link>
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box component="form" onSubmit={handleResetPassword} sx={{ mt: 1, width: '100%' }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Instrucciones:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Revisa tu correo electrónico</li>
                    <li>Copia el token que recibiste</li>
                    <li>Pega el token en el campo inferior</li>
                    <li>Ingresa tu nueva contraseña</li>
                  </ul>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  ⚠️ El token es válido por 1 hora y solo puede usarse una vez.
                </Typography>
              </Alert>

              <TextField
                margin="normal"
                required
                fullWidth
                label="Token (del email)"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                disabled={loading}
                placeholder="Pega el token que recibiste por email"
                autoFocus
                sx={{ mb: 2 }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="Nueva Contraseña"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        aria-label="mostrar contraseña"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Mínimo 6 caracteres"
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Confirmar Nueva Contraseña"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                        aria-label="mostrar contraseña"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  sx={{ flex: 1 }}
                  disabled={loading}
                >
                  Volver
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ flex: 1 }}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {loading ? 'Restableciendo...' : 'Restablecer Contraseña'}
                </Button>
              </Box>
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ cursor: 'pointer' }}
                >
                  Volver al inicio de sesión
                </Link>
              </Box>
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              mt: 3,
              color: 'text.secondary',
              fontSize: '0.7rem',
              opacity: 0.6,
            }}
          >
            v{buildInfo.version} Build {buildInfo.buildNumber}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;

